import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { getApiBaseUrl } from "../data/authStorage";

const emptyEducation = { school: "", degree: "", course: "", qualificationClass: "", startYear: "", endYear: "" };
const emptyExperience = { company: "", position: "", employmentType: "", start: "", end: "", description: "" };
const emptyAwards = { nameOfAward: "", issuedBy: "", awardDate: "", expiryDate: "", certification: "", certificationBody: "", certificateExpiryDate: "" };

const degreeOptions = ["BSc", "B. Tech", "HND", "MSC", "ND", "PhD"];
const genderOptions = ["Male", "Female"];

const personalFields = [
  { key: "firstName", label: "First Name", required: true },
  { key: "lastName", label: "Last Name", required: true },
  { key: "personalEmail", label: "Personal Email", required: true, type: "email" },
  { key: "phone", label: "Phone Number", required: true },
  { key: "gender", label: "Gender", required: true, type: "select", options: genderOptions },
  { key: "maritalStatus", label: "Marital Status", required: true },
  { key: "dob", label: "Date of Birth", required: true, type: "date" },
  { key: "stateOrigin", label: "State of Origin", required: true },
  { key: "stateResidence", label: "State of Residence", required: true },
  { key: "lga", label: "LGA", required: true },
  { key: "residentialAddress", label: "Residential Address", required: true, colSpan: "md:col-span-2" },
];

const educationFields = [
  { key: "school", label: "School" },
  { key: "degree", label: "Degree", type: "select", options: degreeOptions },
  { key: "course", label: "Course" },
  { key: "qualificationClass", label: "Qualification Class" },
  { key: "startYear", label: "Start Year", type: "number" },
  { key: "endYear", label: "End Year", type: "number" },
];

const experienceFields = [
  { key: "company", label: "Company" },
  { key: "position", label: "Position" },
  { key: "employmentType", label: "Employment Type" },
  { key: "start", label: "Start Date", type: "date" },
  { key: "end", label: "End Date", type: "date" },
  { key: "description", label: "Description", colSpan: "md:col-span-2" },
];

const awardsFields = [
  { key: "nameOfAward", label: "Name of Award" },
  { key: "issuedBy", label: "Issued By" },
  { key: "awardDate", label: "Award Date", type: "date" },
  { key: "expiryDate", label: "Award Expiry Date", type: "date" },
  { key: "certification", label: "Certification" },
  { key: "certificationBody", label: "Certification Body" },
  { key: "certificateExpiryDate", label: "Certificate Expiry Date", type: "date" },
];

const steps = ["Personal Information", "Education", "Experience", "Awards"];

const createTouchedRecord = (fields) =>
  fields.reduce((acc, field) => {
    acc[field.key] = false;
    return acc;
  }, {});

const createAllTouchedRecord = (fields) =>
  fields.reduce((acc, field) => {
    acc[field.key] = true;
    return acc;
  }, {});

const hasError = (errors) => Object.keys(errors || {}).length > 0;

const validateEmail = (value) => /[^\s@]+@[^\s@]+\.[^\s@]+/.test(value);

const InviteOnboarding = () => {
  const [searchParams] = useSearchParams();
  const token = `${searchParams.get("token") || ""}`.trim();
  const apiBaseUrl = getApiBaseUrl();

  const [step, setStep] = useState(1);
  const [loadingInvite, setLoadingInvite] = useState(true);
  const [inviteMeta, setInviteMeta] = useState(null);
  const [error, setError] = useState("");
  const [submitLoading, setSubmitLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [stepAlert, setStepAlert] = useState("");

  const [personal, setPersonal] = useState({
    firstName: "",
    lastName: "",
    personalEmail: "",
    phone: "",
    gender: "",
    maritalStatus: "",
    dob: "",
    residentialAddress: "",
    lga: "",
    stateOrigin: "",
    stateResidence: "",
  });

  const [education, setEducation] = useState([{ ...emptyEducation }]);
  const [experience, setExperience] = useState([{ ...emptyExperience }]);
  const [awards, setAwards] = useState([{ ...emptyAwards }]);
  const [personalTouched, setPersonalTouched] = useState(() => createTouchedRecord(personalFields));
  const [educationTouched, setEducationTouched] = useState(() => [createTouchedRecord(educationFields)]);
  const [experienceTouched, setExperienceTouched] = useState(() => [createTouchedRecord(experienceFields)]);
  const [awardsTouched, setAwardsTouched] = useState(() => [createTouchedRecord(awardsFields)]);
  const [submitAttempted, setSubmitAttempted] = useState(false);

  useEffect(() => {
    const validateInvite = async () => {
      if (!token) {
        setError("Invite token is missing.");
        setLoadingInvite(false);
        return;
      }

      if (!apiBaseUrl) {
        setError("API base URL is not configured.");
        setLoadingInvite(false);
        return;
      }

      try {
        setLoadingInvite(true);
        setError("");
        setStepAlert("");

        const response = await axios.get(`${apiBaseUrl}/organizations/employees/invite/validate`, {
          params: { token },
        });

        const invite = response?.data?.invite || null;
        setInviteMeta(invite);
        if (invite?.invitedEmail) {
          setPersonal((prev) => ({ ...prev, personalEmail: prev.personalEmail || invite.invitedEmail }));
        }
      } catch (requestError) {
        const message = requestError?.response?.data?.message || "Invite link is invalid or expired.";
        setError(Array.isArray(message) ? message.join(", ") : message);
      } finally {
        setLoadingInvite(false);
      }
    };

    validateInvite();
  }, [apiBaseUrl, token]);

  const personalErrors = useMemo(() => {
    const nextErrors = {};

    personalFields.forEach((field) => {
      const value = `${personal[field.key] || ""}`.trim();
      if (field.required && !value) {
        nextErrors[field.key] = `${field.label} is required.`;
      }
    });

    const personalEmail = `${personal.personalEmail || ""}`.trim();
    if (personalEmail && !validateEmail(personalEmail)) {
      nextErrors.personalEmail = "Please enter a valid email address.";
    }

    const phone = `${personal.phone || ""}`.trim();
    if (phone && phone.replace(/\D/g, "").length < 7) {
      nextErrors.phone = "Phone number looks too short.";
    }

    return nextErrors;
  }, [personal]);

  const educationErrors = useMemo(
    () =>
      education.map((item) => {
        const rowErrors = {};
        educationFields.forEach((field) => {
          if (!`${item[field.key] || ""}`.trim()) {
            rowErrors[field.key] = `${field.label} is required.`;
          }
        });
        return rowErrors;
      }),
    [education],
  );

  const experienceErrors = useMemo(
    () => experience.map(() => ({})),
    [experience],
  );

  const awardsErrors = useMemo(
    () => awards.map(() => ({})),
    [awards],
  );

  const personalValid = !hasError(personalErrors);
  const educationValid = education.length > 0 && !educationErrors.some(hasError);

  const updateListItem = (setter, index, key, value, touchedSetter) => {
    setter((prev) => prev.map((item, idx) => (idx === index ? { ...item, [key]: value } : item)));
    if (submitAttempted) {
      touchedSetter((prev) => prev.map((item, idx) => (idx === index ? { ...item, [key]: true } : item)));
    }
  };

  const addListItem = (setter, template, touchedSetter, fields) => {
    setter((prev) => [...prev, { ...template }]);
    touchedSetter((prev) => [...prev, createTouchedRecord(fields)]);
  };

  const showPersonalError = (key) => submitAttempted || personalTouched[key];
  const showCollectionError = (touchedList, rowIndex, key) => submitAttempted || touchedList?.[rowIndex]?.[key];

  const markCurrentStepTouched = () => {
    if (step === 1) {
      setPersonalTouched((prev) => {
        const nextTouched = { ...prev };
        personalFields.forEach((field) => {
          nextTouched[field.key] = true;
        });
        return nextTouched;
      });
      return;
    }

    if (step === 2) {
      setEducationTouched((prev) => prev.map(() => createAllTouchedRecord(educationFields)));
      return;
    }

    if (step === 3) {
      setExperienceTouched((prev) => prev.map(() => createAllTouchedRecord(experienceFields)));
      return;
    }

    setAwardsTouched((prev) => prev.map(() => createAllTouchedRecord(awardsFields)));
  };

  const handleContinue = () => {
    if (step === 1 && !personalValid) {
      markCurrentStepTouched();
      setStepAlert("Please complete all required personal information fields.");
      return;
    }

    if (step === 2 && !educationValid) {
      markCurrentStepTouched();
      setStepAlert("Please complete all required education fields.");
      return;
    }

    setStepAlert("");
    setStep((prev) => Math.min(4, prev + 1));
  };

  const handleSubmit = async () => {
    if (!apiBaseUrl) {
      setError("API base URL is not configured.");
      return;
    }

    if (!token) {
      setError("Invite token is missing.");
      return;
    }

    if (!personalValid || !educationValid) {
      setSubmitAttempted(true);
      markCurrentStepTouched();
      setStepAlert("Please complete all required fields before submitting.");
      return;
    }

    try {
      setSubmitLoading(true);
      setError("");
      setStepAlert("");

      await axios.post(`${apiBaseUrl}/organizations/employees/invite/submit`, {
        token,
        firstName: personal.firstName.trim(),
        lastName: personal.lastName.trim(),
        personalEmail: personal.personalEmail.trim(),
        phone: personal.phone.trim(),
        gender: personal.gender.trim(),
        maritalStatus: personal.maritalStatus.trim(),
        dob: personal.dob,
        residentialAddress: personal.residentialAddress.trim(),
        lga: personal.lga.trim(),
        stateOrigin: personal.stateOrigin.trim(),
        stateResidence: personal.stateResidence.trim(),
        educationRecords: JSON.stringify(education),
        experienceRecords: JSON.stringify(
          experience.filter((item) =>
            experienceFields.some((field) => `${item[field.key] || ""}`.trim()),
          ),
        ),
        awardsRecords: JSON.stringify(
          awards.filter((item) =>
            awardsFields.some((field) => `${item[field.key] || ""}`.trim()),
          ),
        ),
      });

      setSuccessMessage(
        "Onboarding details submitted successfully. Your admin will complete organization setup and move your profile to Approved.",
      );
    } catch (requestError) {
      const message = requestError?.response?.data?.message || "Unable to submit onboarding details.";
      setError(Array.isArray(message) ? message.join(", ") : message);
    } finally {
      setSubmitLoading(false);
    }
  };

  if (loadingInvite) {
    return <div className="min-h-screen grid place-items-center font-Outfit text-gray-600">Validating invite...</div>;
  }

  if (error && !inviteMeta) {
    return <div className="min-h-screen grid place-items-center px-6 font-Outfit text-red-600">{error}</div>;
  }

  return (
    <div className="min-h-screen bg-[#f5f7ff] py-8 px-4 font-Outfit">
      <div className="mx-auto max-w-4xl bg-white border border-gray-100 rounded-2xl shadow-sm p-6 space-y-6">
        <div>
          <h1 className="text-2xl font-semibold text-[#071C50]">Staff Self Onboarding</h1>
          <p className="text-sm text-gray-600 mt-1">
            Organization: <span className="font-medium">{inviteMeta?.organizationName || "-"}</span>
          </p>
          <p className="text-sm text-gray-600">
            Invited email: <span className="font-medium">{inviteMeta?.invitedEmail || "-"}</span>
          </p>
        </div>

        <div className="space-y-3">
          <div className="h-2 w-full overflow-hidden rounded-full bg-[#E6EBFF]">
            <div
              className="h-full rounded-full bg-[#1234CD] transition-all"
              style={{ width: `${(step / 4) * 100}%` }}
            />
          </div>

          <div className="flex flex-wrap gap-2 text-sm">
            {steps.map((label, index) => (
              <span
                key={label}
                className={`px-3 py-1 rounded-full ${step === index + 1 ? "bg-[#1234CD] text-white" : "bg-gray-100 text-gray-600"}`}
              >
                {index + 1}. {label}
              </span>
            ))}
          </div>
        </div>

        {error ? <p className="text-sm text-red-600" role="alert">{error}</p> : null}
        {stepAlert ? <p className="text-sm text-red-600" role="alert">{stepAlert}</p> : null}
        {successMessage ? <p className="text-sm text-green-600">{successMessage}</p> : null}

        {step === 1 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {personalFields.map((field) => (
              <label key={field.key} className={`flex flex-col gap-1 text-sm ${field.colSpan || ""}`}>
                <span className="font-medium text-[#071C50]">{field.label}</span>
                {field.type === "select" ? (
                  <select
                    value={personal[field.key] || ""}
                    onChange={(event) => {
                      const nextValue = event.target.value;
                      setPersonal((prev) => ({ ...prev, [field.key]: nextValue }));
                      if (submitAttempted) {
                        setPersonalTouched((prev) => ({ ...prev, [field.key]: true }));
                      }
                    }}
                    onBlur={() => setPersonalTouched((prev) => ({ ...prev, [field.key]: true }))}
                    className={`rounded-lg border px-3 py-2 outline-none focus:border-[#1234CD] ${
                      showPersonalError(field.key) && personalErrors[field.key] ? "border-red-300" : "border-gray-300"
                    }`}
                  >
                    <option value="">Select {field.label.toLowerCase()}</option>
                    {(field.options || []).map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    type={field.type || "text"}
                    value={personal[field.key] || ""}
                    onChange={(event) => {
                      const nextValue = event.target.value;
                      setPersonal((prev) => ({ ...prev, [field.key]: nextValue }));
                      if (submitAttempted) {
                        setPersonalTouched((prev) => ({ ...prev, [field.key]: true }));
                      }
                    }}
                    onBlur={() => setPersonalTouched((prev) => ({ ...prev, [field.key]: true }))}
                    className={`rounded-lg border px-3 py-2 outline-none focus:border-[#1234CD] ${
                      showPersonalError(field.key) && personalErrors[field.key] ? "border-red-300" : "border-gray-300"
                    }`}
                  />
                )}
                {showPersonalError(field.key) && personalErrors[field.key] ? (
                  <span className="text-xs text-red-600">{personalErrors[field.key]}</span>
                ) : null}
              </label>
            ))}
          </div>
        ) : null}

        {step === 2 ? (
          <div className="space-y-4">
            {education.map((item, index) => (
              <div key={`edu-${index}`} className="grid grid-cols-1 md:grid-cols-2 gap-4 border border-gray-100 rounded-xl p-4">
                {educationFields.map((field) => (
                  <label key={field.key} className={`flex flex-col gap-1 text-sm ${field.colSpan || ""}`}>
                    <span className="font-medium text-[#071C50]">{field.label}</span>
                    {field.type === "select" ? (
                      <select
                        value={item[field.key] || ""}
                        onChange={(event) =>
                          updateListItem(setEducation, index, field.key, event.target.value, setEducationTouched)
                        }
                        onBlur={() =>
                          setEducationTouched((prev) =>
                            prev.map((row, rowIndex) =>
                              rowIndex === index ? { ...row, [field.key]: true } : row,
                            ),
                          )
                        }
                        className={`rounded-lg border px-3 py-2 outline-none focus:border-[#1234CD] ${
                          showCollectionError(educationTouched, index, field.key) && educationErrors[index]?.[field.key]
                            ? "border-red-300"
                            : "border-gray-300"
                        }`}
                      >
                        <option value="">Select degree</option>
                        {(field.options || []).map((option) => (
                          <option key={option} value={option}>
                            {option}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <input
                        type={field.type || "text"}
                        value={item[field.key] || ""}
                        onChange={(event) =>
                          updateListItem(setEducation, index, field.key, event.target.value, setEducationTouched)
                        }
                        onBlur={() =>
                          setEducationTouched((prev) =>
                            prev.map((row, rowIndex) =>
                              rowIndex === index ? { ...row, [field.key]: true } : row,
                            ),
                          )
                        }
                        className={`rounded-lg border px-3 py-2 outline-none focus:border-[#1234CD] ${
                          showCollectionError(educationTouched, index, field.key) && educationErrors[index]?.[field.key]
                            ? "border-red-300"
                            : "border-gray-300"
                        }`}
                      />
                    )}
                    {showCollectionError(educationTouched, index, field.key) && educationErrors[index]?.[field.key] ? (
                      <span className="text-xs text-red-600">{educationErrors[index][field.key]}</span>
                    ) : null}
                  </label>
                ))}
              </div>
            ))}
            <button
              type="button"
              className="text-sm text-[#1234CD]"
              onClick={() => addListItem(setEducation, emptyEducation, setEducationTouched, educationFields)}
            >
              + Add Education
            </button>
          </div>
        ) : null}

        {step === 3 ? (
          <div className="space-y-4">
            {experience.map((item, index) => (
              <div key={`exp-${index}`} className="grid grid-cols-1 md:grid-cols-2 gap-4 border border-gray-100 rounded-xl p-4">
                {experienceFields.map((field) => (
                  <label key={field.key} className={`flex flex-col gap-1 text-sm ${field.colSpan || ""}`}>
                    <span className="font-medium text-[#071C50]">{field.label}</span>
                    <input
                      type={field.type || "text"}
                      value={item[field.key] || ""}
                      onChange={(event) =>
                        updateListItem(setExperience, index, field.key, event.target.value, setExperienceTouched)
                      }
                      onBlur={() =>
                        setExperienceTouched((prev) =>
                          prev.map((row, rowIndex) =>
                            rowIndex === index ? { ...row, [field.key]: true } : row,
                          ),
                        )
                      }
                      className={`rounded-lg border px-3 py-2 outline-none focus:border-[#1234CD] ${
                        showCollectionError(experienceTouched, index, field.key) && experienceErrors[index]?.[field.key]
                          ? "border-red-300"
                          : "border-gray-300"
                      }`}
                    />
                    {showCollectionError(experienceTouched, index, field.key) && experienceErrors[index]?.[field.key] ? (
                      <span className="text-xs text-red-600">{experienceErrors[index][field.key]}</span>
                    ) : null}
                  </label>
                ))}
              </div>
            ))}
            <button
              type="button"
              className="text-sm text-[#1234CD]"
              onClick={() => addListItem(setExperience, emptyExperience, setExperienceTouched, experienceFields)}
            >
              + Add Experience
            </button>
          </div>
        ) : null}

        {step === 4 ? (
          <div className="space-y-4">
            {awards.map((item, index) => (
              <div key={`award-${index}`} className="grid grid-cols-1 md:grid-cols-2 gap-4 border border-gray-100 rounded-xl p-4">
                {awardsFields.map((field) => (
                  <label key={field.key} className={`flex flex-col gap-1 text-sm ${field.colSpan || ""}`}>
                    <span className="font-medium text-[#071C50]">{field.label}</span>
                    <input
                      type={field.type || "text"}
                      value={item[field.key] || ""}
                      onChange={(event) => updateListItem(setAwards, index, field.key, event.target.value, setAwardsTouched)}
                      onBlur={() =>
                        setAwardsTouched((prev) =>
                          prev.map((row, rowIndex) =>
                            rowIndex === index ? { ...row, [field.key]: true } : row,
                          ),
                        )
                      }
                      className={`rounded-lg border px-3 py-2 outline-none focus:border-[#1234CD] ${
                        showCollectionError(awardsTouched, index, field.key) && awardsErrors[index]?.[field.key]
                          ? "border-red-300"
                          : "border-gray-300"
                      }`}
                    />
                    {showCollectionError(awardsTouched, index, field.key) && awardsErrors[index]?.[field.key] ? (
                      <span className="text-xs text-red-600">{awardsErrors[index][field.key]}</span>
                    ) : null}
                  </label>
                ))}
              </div>
            ))}
            <button
              type="button"
              className="text-sm text-[#1234CD]"
              onClick={() => addListItem(setAwards, emptyAwards, setAwardsTouched, awardsFields)}
            >
              + Add Award/Certification
            </button>
          </div>
        ) : null}

        <div className="flex items-center justify-between">
          <button
            type="button"
            onClick={() => {
              setStep((prev) => Math.max(1, prev - 1));
              setStepAlert("");
            }}
            disabled={step === 1 || submitLoading}
            className="rounded-lg border border-gray-300 px-4 py-2 text-sm disabled:opacity-50"
          >
            Back
          </button>

          {step < 4 ? (
            <button
              type="button"
              onClick={handleContinue}
              disabled={submitLoading}
              className="rounded-lg bg-[#1234CD] px-4 py-2 text-sm text-white disabled:opacity-50"
            >
              Continue
            </button>
          ) : (
            <button
              type="button"
              onClick={() => {
                setSubmitAttempted(true);
                handleSubmit();
              }}
              disabled={submitLoading || !!successMessage}
              className="rounded-lg bg-[#1234CD] px-4 py-2 text-sm text-white disabled:opacity-50"
            >
              {submitLoading ? "Submitting..." : "Submit Onboarding"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default InviteOnboarding;
